import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Loader2, Repeat, Pause, Play, X, RefreshCw, CalendarClock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import RecurringFrequencyFields from "./RecurringFrequencyFields";

const STATUS_PILL = {
  active:    { bg: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Active" },
  paused:    { bg: "bg-amber-100 text-amber-800 border-amber-200",       label: "Paused" },
  cancelled: { bg: "bg-rose-100 text-rose-800 border-rose-200",         label: "Cancelled" },
};

export default function RecurringScheduleCard({ services = [], reloadKey = 0 }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ schedule: null, next_service: null, last_service: null });
  const [busy, setBusy] = useState(null);
  const [freqOpen, setFreqOpen] = useState(false);
  const [freqForm, setFreqForm] = useState({ service_frequency: "biweekly" });
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedForm, setReschedForm] = useState({ preferred_date: "", preferred_time_window: "morning", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/me/recurring");
      setData(d || {});
    } catch { setData({ schedule: null }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [reloadKey]);

  const sched = data.schedule;
  const status = sched?.status || "active";
  const pill = STATUS_PILL[status] || STATUS_PILL.active;

  const act = async (path, body = undefined) => {
    setBusy(path);
    try {
      await api.post(`/me/recurring/${path}`, body);
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(null); }
  };

  const submitFrequency = async () => {
    if (!freqForm.service_frequency) return;
    setBusy("change");
    try {
      await api.post("/me/recurring/change-frequency", freqForm);
      toast.success("Frequency updated");
      setFreqOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change frequency");
    } finally { setBusy(null); }
  };

  const submitReschedule = async () => {
    if (!reschedForm.preferred_date) return toast.error("Pick a date");
    setBusy("reschedule");
    try {
      await api.post("/me/recurring/request-reschedule", reschedForm);
      toast.success("Reschedule request sent. JDSN will confirm shortly.");
      setReschedOpen(false);
      setReschedForm({ preferred_date: "", preferred_time_window: "morning", note: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-3xl border border-gray-200 p-6" data-testid="recurring-card-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#047857]" />
      </div>
    );
  }

  if (!sched || sched.frequency === "one_time") {
    return (
      <div className="bg-gradient-to-br from-[#062818] to-[#047857] text-white rounded-3xl p-6 sm:p-7" data-testid="recurring-card-empty">
        <div className="flex items-center gap-2 text-emerald-100">
          <Repeat className="w-4 h-4" />
          <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold">Set up recurring service</div>
        </div>
        <h3 className="mt-1 text-2xl font-extrabold tracking-tight font-[Outfit]">Never schedule again</h3>
        <p className="mt-1.5 text-sm text-emerald-100/85 max-w-md">
          Tell us how often you want service and we&apos;ll keep your lawn looking sharp on auto-pilot. Pause, skip, or cancel anytime.
        </p>
        <div className="mt-4">
          <Button
            onClick={() => { setFreqForm({ service_frequency: "biweekly" }); setFreqOpen(true); }}
            className="rounded-full bg-white text-[#047857] font-extrabold hover:bg-emerald-50"
            data-testid="recurring-setup-btn"
          >
            Set up recurring service
          </Button>
        </div>
        <FrequencyDialog open={freqOpen} setOpen={setFreqOpen} form={freqForm} setForm={setFreqForm} services={services} submit={submitFrequency} busy={busy} title="Set up recurring service" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-gray-200 p-5 sm:p-6 shadow-sm" data-testid="recurring-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[#047857]">
            <Repeat className="w-4 h-4" />
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold">My recurring service</div>
            <span className={`text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full border ${pill.bg}`} data-testid="recurring-status-pill">{pill.label}</span>
          </div>
          <h3 className="mt-1 text-xl sm:text-2xl font-extrabold tracking-tight font-[Outfit] text-gray-900" data-testid="recurring-frequency-label">{sched.frequency_label}</h3>
          <div className="mt-1 text-[13px] text-gray-600">
            {sched.preferred_service_day && sched.preferred_service_day !== "any" ? `${sched.preferred_service_day.toUpperCase()} · ` : ""}
            {sched.preferred_time_window ? sched.preferred_time_window : ""}
          </div>
        </div>
        <Button size="sm" variant="ghost" onClick={load} className="rounded-full" data-testid="recurring-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <Tile label="Next service" value={data.next_service ? `${data.next_service.preferred_date} · ${data.next_service.service_name}` : "—"} testid="recurring-next" />
        <Tile label="Last service" value={data.last_service?.preferred_date || "—"} testid="recurring-last" />
      </div>

      {/* iter253 — Paused-until banner: whenever the customer's recurring
          schedule is paused with a resume date, surface it right on the
          Dashboard so they can see when service will restart without
          re-opening the Pause dialog. */}
      {status === "paused" && sched?.resume_at && (
        <div className="mt-3 rounded-2xl border-2 border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-2.5"
             data-testid="recurring-paused-until">
          <CalendarClock className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-amber-800">Service paused until</div>
            <div className="mt-0.5 text-[15px] font-extrabold text-amber-950 leading-tight" data-testid="recurring-paused-until-date">
              {formatPausedUntil(sched.resume_at)}
            </div>
            <div className="mt-0.5 text-[11px] font-semibold text-amber-800/90">
              We&apos;ll automatically restart your recurring visits on this date.
            </div>
          </div>
        </div>
      )}

      {/* Payment CTA moved to the single "Payment Due" section at the top of
          the dashboard (iter125). No per-card Pay Now buttons anywhere. */}

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {status === "active" && (
          <Button onClick={() => act("pause")} disabled={busy} variant="outline" className="rounded-full" data-testid="recurring-pause"><Pause className="w-4 h-4 mr-1.5" />Pause</Button>
        )}
        {status === "paused" && (
          <Button onClick={() => act("resume")} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="recurring-resume"><Play className="w-4 h-4 mr-1.5" />Resume</Button>
        )}
        <Button onClick={() => { setFreqForm({ service_frequency: sched.frequency }); setFreqOpen(true); }} disabled={busy} variant="outline" className="rounded-full" data-testid="recurring-change-frequency"><Sparkles className="w-4 h-4 mr-1.5" />Change frequency</Button>
        <Button onClick={() => setReschedOpen(true)} disabled={busy} variant="outline" className="rounded-full" data-testid="recurring-reschedule"><CalendarClock className="w-4 h-4 mr-1.5" />Reschedule</Button>
        {status !== "cancelled" && (
          <Button onClick={() => {
            if (window.confirm("Cancel your recurring service? You can re-enable it any time.")) act("cancel");
          }} disabled={busy} variant="outline" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="recurring-cancel"><X className="w-4 h-4 mr-1.5" />Cancel</Button>
        )}
      </div>

      <FrequencyDialog open={freqOpen} setOpen={setFreqOpen} form={freqForm} setForm={setFreqForm} services={services} submit={submitFrequency} busy={busy} title="Change frequency" />

      <Dialog open={reschedOpen} onOpenChange={setReschedOpen}>
        <DialogContent className="sm:max-w-md" data-testid="recurring-reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Request a reschedule</DialogTitle>
            <DialogDescription>Tell JDSN when you'd like your next visit. We'll confirm by SMS or email.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Preferred new date</Label>
              <Input type="date" value={reschedForm.preferred_date} onChange={(e) => setReschedForm({ ...reschedForm, preferred_date: e.target.value })} data-testid="reschedule-date" />
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={3} value={reschedForm.note} onChange={(e) => setReschedForm({ ...reschedForm, note: e.target.value })} placeholder="Vacation, weather, etc." data-testid="reschedule-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setReschedOpen(false)}>Cancel</Button>
            <Button onClick={submitReschedule} disabled={busy === "reschedule"} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid="reschedule-submit">
              {busy === "reschedule" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FrequencyDialog({ open, setOpen, form, setForm, services, submit, busy, title }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="recurring-frequency-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>Pick a cadence that works for your lawn. You can change it anytime.</DialogDescription>
        </DialogHeader>
        <RecurringFrequencyFields value={form} onChange={setForm} services={services} showServiceFields={false} />
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy === "change"} className="rounded-full bg-[#047857] hover:bg-[#065F46]" data-testid="recurring-frequency-submit">
            {busy === "change" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-gray-200 px-3 py-2.5 bg-gray-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-extrabold text-gray-500">{label}</div>
      <div className="mt-0.5 text-[13px] font-bold text-gray-900 truncate">{value}</div>
    </div>
  );
}

// iter253 — Format YYYY-MM-DD in the customer's LOCAL calendar so the
// paused-until banner never shifts a day due to timezone conversion.
function formatPausedUntil(ymd) {
  if (!ymd || typeof ymd !== "string") return ymd || "—";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}
